import { apiResetRoomCharm } from '@/api/room'

/**
 * 统一声明一个函数，
 * App可调这方法，传函数名执行相当于代码，
 * 再通过window异步通知App
 *
 * webNative('函数名')
 * 封装统一一个方法给App，
 * 比如App想获取 getMicQueueList 就 调webNative('getMicQueueList')。
 * 返回给App 是 JSON.stringify(message) ，message 格式是 {type: '函数名',data: 数据}
 * 然后App统一处理
 * **/
export default defineNuxtPlugin(() => {
  //#region webNative 客户端调用web端方法封装
  const rnFun: any = {
    getNative: 'native'
  }
  // 提前声明方法提供给App调取模块
  // 提前声明函数名，有函数命才可以调取
  const appNativeFunNames = {
    getMicQueueList: 'getMicQueueList', // 获麦位列表
    sendEmoji: 'sendEmoji', // 发送表情
    setChatZoneSwitch: 'setChatZoneSwitch', // 设置公屏开关
    setRideEffectSwitch: 'setRideEffectSwitch', // 设置座驾特效开关
    getRoomInfo: 'getRoomInfo', // 获取房间信息
    openMoreSetting: 'openMoreSetting', // 打开更多设置(低价礼物特效、公屏开关)
    openReportUser: 'openReportUser', // 打开举报弹窗
    sendInviteUserUpMic: 'sendInviteUserUpMic', // 发送邀请用户上麦im
    closeRoom: 'closeRoom', // 关闭房间(退出前调im退房)
    clearCharmValue: 'clearCharmValue', // 清空魅力值
    sendGroupSubjectSendText: 'sendGroupSubjectSendText', // 客户端通知发送groupSubjectSendText im
    openMoreAction: 'openMoreAction', // 资料卡片打开更多操作
    getCurrentRole: 'getCurrentRole', // 获取当前用户角色权限
    getOnlineUserNum: 'getOnlineUserNum', // 获取房间在线用户数
    updateLanguage: 'updateLanguage', // 客户端修改语言后通知更新
    hadOpenBag: 'hadOpenBag', // 抢完幸运红包，更新状态
    updateUserInfo: 'updateUserInfo', // 客户端关注/取关用户时通知更新 param: {uid:123456}
    socketMessage: 'socketMessage', // socket Message
    atUser: 'atUser' // 房间公屏艾特用户 param: {atUid: 11643, atNick: '港风人'}
  }
  /**
   * 安卓和IOS可以直接调这个方法获取数据
   * @param {string} funName 函数名
   * @param {object} data 需要传递的数据
   */
  function webNative(funName: string, data: { [key in string]: any }) {
    const version = common.checkVersion()
    // 校验环境 && 没有函数名 就return
    if (version.app && !appNativeFunNames[funName]) return

    // 设置数据，最后会转成JSONString
    const i18n = useNuxtApp().$i18n
    const audioStore = useAudioStore()
    const emojiStore = useEmojiStore()
    const roomStore = useRoomStore()
    const publicStore = usePublicStore()
    const socketStore = useSocketStore()
    const languageStore = useLanguageStore()
    const luckyBagStore = useLuckyBagStore()
    const userStore = useUserStore()

    const message = {
      type: funName, // 函数名
      data: null as any // 数据
    }
    console.log('webNative => ', funName, data)

    switch (funName) {
      // socket回调的消息
      case appNativeFunNames.socketMessage:
        // 心跳路由不console.log
        if (data.route !== 'heartbeat') {
          console.log('appBridge => socketMessage', data)
        }
        socketStore.onMsg(typeof data === 'string' ? JSON.parse(data) : data)
        return
      // 获麦位列表
      case appNativeFunNames.getMicQueueList:
        message.data = {
          queueList:
            roomStore.roomType !== constant.ROOM_TYPE.AUDIO_LIVE
              ? [audioStore.hostMinInfo]
              : audioStore.queueList.some(
                    (item) =>
                      item.value.member?.uid === roomStore.anchorInfo.uid
                  )
                ? audioStore.queueList
                : [audioStore.hostMinInfo, ...audioStore.queueList]
        }
        break
      // 发送表情
      case appNativeFunNames.sendEmoji:
        data?.faceId && emojiStore.sendEmoji(data.faceId)
        // 该方法不需要回调客户端
        return
      // 设置公屏开关
      case appNativeFunNames.setChatZoneSwitch:
        data?.switch !== undefined &&
          roomStore.setRoomConfigSwitch(data.switch, 'chatZone')
        return
      // 设置座驾特效开关
      case appNativeFunNames.setRideEffectSwitch:
        data?.switch !== undefined &&
          roomStore.setRoomConfigSwitch(data.switch, 'rideEffect')
        return
      // 获取房间信息
      case appNativeFunNames.getRoomInfo:
        message.data = {
          ...roomStore.roomInfo,
          erbanNo: roomStore.anchorInfo.erbanNo
        }
        break
      // 打开更多设置
      case appNativeFunNames.openMoreSetting:
        publicStore.setActionSheetConfig({
          isShow: true,
          options: [
            {
              name:
                roomStore.socketRoomInfo.giftEffectSwitch !== 1
                  ? i18n.t('room.LowValueStop')
                  : i18n.t('room.LowValueStarted'),
              callback: () => {
                roomStore.setRoomConfigSwitch(
                  roomStore.socketRoomInfo.giftEffectSwitch === 1,
                  'lowValueGift'
                )
              }
            },
            {
              name:
                roomStore.socketRoomInfo.publicChatSwitch !== 1
                  ? i18n.t('room.CloseChatZone')
                  : i18n.t('room.OpenChatZone'),
              callback: () => {
                roomStore.setRoomConfigSwitch(
                  roomStore.socketRoomInfo.publicChatSwitch === 1,
                  'chatZone'
                )
              }
            }
          ]
        })
        return
      // 举报用户
      case appNativeFunNames.openReportUser:
        data.uid &&
          publicStore.setReportPopupConfig({
            isShow: true,
            reportUid: data.uid
          })
        return
      // 发送邀请用户上麦
      case appNativeFunNames.sendInviteUserUpMic:
        data.uid && audioStore.sendInviteUserUpMic(data.uid)
        return
      // 退出房间
      case appNativeFunNames.closeRoom:
        roomStore.quit().then(() => {
          window['appFun']('closeRoom')
        })
        return
      // 清空魅力值
      case appNativeFunNames.clearCharmValue:
        showConfirmDialog({
          message: i18n.t('room.ClearCharisma'),
          confirmButtonText: i18n.t('public.Confirm'),
          cancelButtonText: i18n.t('editProfile.Cancle'),
          className: 'private-dialog-customize private-dialog-customize-IOS'
        })
          .then(() => {
            apiResetRoomCharm({ roomId: roomStore.roomId })
          })
          .catch(() => {})
        return
      // 客户端通知发送groupSubjectSendText im
      case appNativeFunNames.sendGroupSubjectSendText:
        socketStore.privateSend({
          route: 'groupSubjectSendText',
          req_data: data
        })
        return
      // 资料卡片打开更多操作(管理员/房主)
      case appNativeFunNames.openMoreAction:
        data.uid && roomStore.handleOpenMoreAction(data.uid)
        return
      // 获取当前用户角色权限
      case appNativeFunNames.getCurrentRole:
        message.data = {
          isCreator: audioStore.isCreator,
          isManager: audioStore.isManager
        }
        break
      // 获取房间在线用户数
      case appNativeFunNames.getOnlineUserNum:
        /**
        1.判断房主是否在房，和在房人数，
          若房主在房，人数则等于右侧在房人数
          若房主不在房，则需要判断人数是否小于或等于100，
            若人数小于或等于，则送礼人数需要+1，
            若人数大于100，则人数则等于右侧在房人数
         */
        message.data = {
          onlineUserNum: Math.min(
            roomStore.isHostLive
              ? socketStore.socketOnlineNum
              : socketStore.socketOnlineNum + 1,
            100
          )
        }
        break
      // 客户端修改语言后更新
      case appNativeFunNames.updateLanguage:
        data.language && languageStore.setLanguageInfo(data.language)
        return
      // 抢完幸运红包，更新状态
      case appNativeFunNames.hadOpenBag:
        luckyBagStore.setLuckyBagHadGrab(true)
        return
      // 客户端关注/取关用户时通知更新 param: {uid:123456}
      case appNativeFunNames.updateUserInfo:
        data.uid && userStore.publicGetUserInfo(data.uid)
        return
      // 房间公屏艾特用户
      case appNativeFunNames.atUser:
        data?.atUid &&
          emitter.emit(
            EmitterEventType.ROOM_CHAT_AT_USER,
            data as { atUid: number; atNick: string }
          )
        return
    }
    console.log('webNative 回调 => ', funName, message.data)

    // iOS 调用方式
    if (version.ios) {
      let appObj: any = {}
      if (window['webkit'].messageHandlers[funName]) {
        appObj = window['webkit'].messageHandlers
      } else {
        appObj = window['ReactNativeWebView']
      }
      // eslint-disable-next-line no-prototype-builtins
      if (appObj.hasOwnProperty('postMessage')) {
        appObj.postMessage(JSON.stringify(message))
      } else {
        if (rnFun[funName]) {
          return
        }
        appObj[funName] && appObj[funName].postMessage(JSON.stringify(message))
      }
    } else {
      // Android 调用方式
      const appObj = window['androidJsObj']
      appObj[funName](JSON.stringify(message))
    }
  }
  window['webNative'] = webNative
  //#endregion

  //#region web方法具体处理
  //#endregion
})
